import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import api from '../config/api';
import { useAuth } from '../context/AuthContext';
import { usePageTitle } from '../context/PageChromeContext';
import { parseQueryConfig } from '../utils/kpiConfig';
import DashboardCharts from '../components/DashboardCharts';
import LoadingButton from '../components/LoadingButton';
import IconButton from '../components/IconButton';
import Modal from '../components/Modal';
import MoreMenu from '../components/MoreMenu';
import { QueryLoadStatus, QueryLoadingPanel } from '../components/QueryLoadStatus';
import { StaggerGrid, StaggerItem, AnimatedPanel, FadeIn, TabFade } from '../components/motion';
import {
  FiPlus,
  FiEdit,
  FiTrash2,
  FiBarChart2,
  FiGrid,
  FiTrendingUp,
  FiRefreshCw,
} from 'react-icons/fi';
import './Dashboard.css';
import { appAlert, appConfirm } from '../utils/appDialog';

const TABS = [
  { id: 'overview', label: 'Overview', icon: FiGrid },
  { id: 'kpis', label: 'KPIs', icon: FiTrendingUp },
  { id: 'analytics', label: 'Analytics', icon: FiBarChart2 },
];

const SUMMARY_LABELS = {
  active_jobs: 'Active Jobs',
  my_matches: 'My Matches',
  my_resumes: 'My Resumes',
  avg_match_score: 'Avg Match Score',
  total_candidates: 'Candidates',
  assigned_candidates: 'Assigned',
  pending_timesheets: 'Pending Timesheets',
  total_users: 'Total Users',
};

/** Stable accent tones for summary metrics */
const SUMMARY_TONES = {
  active_jobs: 1,
  my_matches: 2,
  my_resumes: 3,
  avg_match_score: 4,
  total_candidates: 7,
  assigned_candidates: 8,
  pending_timesheets: 5,
  total_users: 6,
};

function summaryTone(key, index) {
  return SUMMARY_TONES[key] || (index % 8) + 1;
}

function buildKpiNavigateUrl(config) {
  if (!config || config.type !== 'candidate_filter') return null;
  const params = new URLSearchParams();
  if (config.conditions?.length) {
    params.set('query', JSON.stringify(config.conditions));
  } else if (config.filters) {
    const conditions = Object.entries(config.filters)
      .filter(([, value]) => value)
      .map(([field, value]) => ({ field, value: String(value), operator: 'like' }));
    if (conditions.length) {
      params.set('query', JSON.stringify(conditions));
    }
  }
  return params.toString() ? `/candidates?${params.toString()}` : null;
}

/**
 * KPI tile. `onOpen` makes the tile a single activatable target; the overflow
 * menu is rendered as a sibling (absolutely placed by CSS) so it never becomes
 * an interactive descendant of that target.
 */
function KpiCard({ kpi, tone, onOpen, actions, hint }) {
  const clickable = Boolean(onOpen);

  return (
    <StaggerItem
      className={`kpi-card kpi-card--tone-${tone}${clickable ? ' kpi-clickable' : ''}${
        actions ? ' kpi-card--has-actions' : ''
      }`}
    >
      {actions ? <div className="kpi-actions">{actions}</div> : null}
      <div
        className="kpi-card-body"
        role={clickable ? 'button' : undefined}
        tabIndex={clickable ? 0 : undefined}
        aria-label={clickable ? `${kpi.name} — view matching candidates` : undefined}
        onClick={onOpen}
        onKeyDown={
          clickable
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onOpen();
                }
              }
            : undefined
        }
      >
        <div className="kpi-header">
          <h3>{kpi.name}</h3>
        </div>
        <div className="kpi-value">{kpi.current_value ?? '—'}</div>
        {kpi.description && <p className="kpi-description">{kpi.description}</p>}
        {clickable && hint && <p className="kpi-hint">Click to view filtered candidates</p>}
      </div>
    </StaggerItem>
  );
}

function KpiEmptyState({ inline, onAdd }) {
  return (
    <div className={`dashboard-empty${inline ? ' dashboard-empty--inline' : ''}`}>
      <FiTrendingUp className="dashboard-empty-icon" aria-hidden />
      <h3>No KPIs yet</h3>
      <p>Pin the numbers you check every day so they are the first thing you see here.</p>
      <button type="button" className="btn btn-primary btn-sm" onClick={onAdd}>
        <FiPlus aria-hidden /> Add your first KPI
      </button>
    </div>
  );
}

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const welcomeTitle = user?.first_name ? `Welcome back, ${user.first_name}` : 'Dashboard';
  usePageTitle(welcomeTitle);
  const [activeTab, setActiveTab] = useState('overview');
  const [showKpiModal, setShowKpiModal] = useState(false);
  const [editingKpi, setEditingKpi] = useState(null);
  const [kpiError, setKpiError] = useState('');
  const [kpiForm, setKpiForm] = useState({
    name: '',
    description: '',
    metric_type: '',
    display_order: 0,
  });

  const {
    data: kpis,
    isLoading: kpisLoading,
    isFetching: kpisFetching,
    error: kpisError,
    refetch: refetchKpis,
  } = useQuery(
    'kpis',
    () => api.get('/kpis/my-kpis').then((res) => res.data),
    { staleTime: 60_000 }
  );

  const [isRefreshing, setIsRefreshing] = useState(false);

  const {
    data: analytics,
    isLoading: analyticsLoading,
    isFetching: analyticsFetching,
    refetch: refetchAnalytics,
  } = useQuery(
    'dashboard-analytics',
    () => api.get('/dashboard/analytics').then((res) => res.data),
    { staleTime: 120_000 }
  );

  const { data: metricTypes, isLoading: isLoadingMetricTypes, error: metricTypesError } = useQuery(
    'metric-types',
    () => api.get('/kpis/metric-types').then((res) => res.data)
  );

  const resetKpiForm = () => {
    setKpiForm({ name: '', description: '', metric_type: '', display_order: 0 });
    setKpiError('');
    setEditingKpi(null);
  };

  const createKpiMutation = useMutation((data) => api.post('/kpis', data), {
    onSuccess: () => {
      queryClient.invalidateQueries('kpis');
      setShowKpiModal(false);
      resetKpiForm();
    },
    onError: (err) => {
      setKpiError(err.response?.data?.error || 'Failed to create KPI');
    },
  });

  const updateKpiMutation = useMutation(({ id, data }) => api.put(`/kpis/${id}`, data), {
    onSuccess: () => {
      queryClient.invalidateQueries('kpis');
      setShowKpiModal(false);
      resetKpiForm();
    },
    onError: (err) => {
      setKpiError(err.response?.data?.error || 'Failed to update KPI');
    },
  });

  const deleteKpiMutation = useMutation((id) => api.delete(`/kpis/${id}`), {
    onSuccess: () => queryClient.invalidateQueries('kpis'),
    onError: (err) => {
      void appAlert(err.response?.data?.error || 'Failed to delete KPI');
    },
  });

  const handleCreateKpi = () => {
    resetKpiForm();
    setShowKpiModal(true);
  };

  const handleEditKpi = (kpi) => {
    setEditingKpi(kpi);
    setKpiForm({
      name: kpi.name,
      description: kpi.description || '',
      metric_type: kpi.metric_type,
      display_order: kpi.display_order ?? 0,
    });
    setKpiError('');
    setShowKpiModal(true);
  };

  const handleDeleteKpi = async (kpi) => {
    if (await appConfirm(`Delete KPI "${kpi.name}"?`)) {
      deleteKpiMutation.mutate(kpi.id);
    }
  };

  const handleSubmitKpi = (e) => {
    e.preventDefault();
    setKpiError('');
    const payload = {
      ...kpiForm,
      display_order: Number.isFinite(Number(kpiForm.display_order))
        ? Number(kpiForm.display_order)
        : 0,
    };
    if (editingKpi) {
      updateKpiMutation.mutate({ id: editingKpi.id, data: payload });
    } else {
      createKpiMutation.mutate(payload);
    }
  };

  const handleKpiClick = (kpi) => {
    const config = parseQueryConfig(kpi.query_config);
    const url = buildKpiNavigateUrl(config);
    if (url) navigate(url);
  };

  const handleRefresh = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      await Promise.all([refetchAnalytics(), refetchKpis()]);
    } finally {
      setIsRefreshing(false);
    }
  };

  const isLoading = kpisLoading && analyticsLoading;

  if (isLoading) {
    return <QueryLoadingPanel isLoading initialLabel="Loading dashboard…" />;
  }

  const summaryEntries = Object.entries(analytics?.summary || {}).filter(
    ([, value]) => value !== null && value !== undefined
  );

  const kpiList = kpis || [];
  const previewKpis = kpiList.slice(0, 4);

  const openKpi = (kpi) => {
    const config = parseQueryConfig(kpi.query_config);
    if (kpi.metric_type !== 'custom_filter' || !buildKpiNavigateUrl(config)) return undefined;
    return () => handleKpiClick(kpi);
  };

  return (
    <div className="dashboard list-page">
      <div className="page-content-layer">
      <div className="dashboard-toolbar">
        <nav className="dashboard-tabs" aria-label="Dashboard sections">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                type="button"
                className={`dashboard-tab ${activeTab === tab.id ? 'active' : ''}`}
                aria-pressed={activeTab === tab.id}
                aria-controls="dashboard-tab-panel"
                onClick={() => setActiveTab(tab.id)}
              >
                <Icon aria-hidden /> {tab.label}
              </button>
            );
          })}
        </nav>

        <FadeIn delay={2} className="dashboard-toolbar-actions page-toolbar">
          <QueryLoadStatus
            isLoading={kpisLoading || analyticsLoading}
            isFetching={kpisFetching || analyticsFetching}
            hasData={Boolean(kpis || analytics)}
          />
          <IconButton
            icon={FiRefreshCw}
            label="Refresh dashboard"
            onClick={handleRefresh}
            loading={isRefreshing}
          />
          <IconButton
            icon={FiPlus}
            label="Add KPI"
            variant="primary"
            onClick={handleCreateKpi}
          />
        </FadeIn>
      </div>

      <div id="dashboard-tab-panel">
      <TabFade tabKey={activeTab} className="dashboard-tab-panel">
      {activeTab === 'overview' && (
        <div className="dashboard-section">
          {summaryEntries.length > 0 && (
            <StaggerGrid className="summary-strip">
              {summaryEntries.map(([key, value], index) => (
                <StaggerItem
                  key={key}
                  className={`summary-chip summary-chip--tone-${summaryTone(key, index)}`}
                >
                  <span className="summary-chip-value">{value}</span>
                  <span className="summary-chip-label">{SUMMARY_LABELS[key] || key}</span>
                </StaggerItem>
              ))}
            </StaggerGrid>
          )}

          <AnimatedPanel className="dashboard-panel">
            <div className="dashboard-panel-head">
              <div className="dashboard-panel-title">
                <h2>Key Metrics</h2>
                {kpiList.length > 0 && (
                  <p className="dashboard-panel-meta">
                    {kpiList.length > previewKpis.length
                      ? `Showing ${previewKpis.length} of ${kpiList.length}`
                      : `${kpiList.length} tracked`}
                  </p>
                )}
              </div>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setActiveTab('kpis')}
              >
                Manage KPIs
              </button>
            </div>
            {kpisError ? (
              <div className="error">Failed to load KPIs. Try refreshing.</div>
            ) : previewKpis.length ? (
              <StaggerGrid className="kpi-grid kpi-grid--compact">
                {previewKpis.map((kpi, index) => (
                  <KpiCard key={kpi.id} kpi={kpi} tone={(index % 8) + 1} onOpen={openKpi(kpi)} />
                ))}
              </StaggerGrid>
            ) : (
              <KpiEmptyState inline onAdd={handleCreateKpi} />
            )}
          </AnimatedPanel>

          <AnimatedPanel className="dashboard-panel" hover={false}>
            <div className="dashboard-panel-head">
              <h2>Analytics Preview</h2>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setActiveTab('analytics')}>
                View all charts
              </button>
            </div>
            <DashboardCharts
              charts={analytics?.charts?.slice(0, 2)}
              isLoading={analyticsLoading}
            />
          </AnimatedPanel>
        </div>
      )}

      {activeTab === 'kpis' && (
        <div className="dashboard-section">
          <AnimatedPanel className="dashboard-panel" hover={false}>
            <div className="dashboard-panel-head">
              <div className="dashboard-panel-title">
                <h2>Your KPIs</h2>
                {kpiList.length > 0 && (
                  <p className="dashboard-panel-meta">
                    {`${kpiList.length} metric${kpiList.length === 1 ? '' : 's'} tracked`}
                  </p>
                )}
              </div>
            </div>
            {kpisError ? (
              <div className="error">Failed to load KPIs. Try refreshing.</div>
            ) : kpiList.length ? (
              <StaggerGrid className="kpi-grid">
                {kpiList.map((kpi, index) => (
                  <KpiCard
                    key={kpi.id}
                    kpi={kpi}
                    tone={(index % 8) + 1}
                    onOpen={openKpi(kpi)}
                    hint
                    actions={
                      <MoreMenu
                        label={`Actions for ${kpi.name}`}
                        size="sm"
                        items={[
                          {
                            id: 'edit',
                            label: 'Edit',
                            icon: FiEdit,
                            onSelect: () => handleEditKpi(kpi),
                          },
                          {
                            id: 'delete',
                            label: 'Delete',
                            icon: FiTrash2,
                            danger: true,
                            disabled:
                              deleteKpiMutation.isLoading &&
                              deleteKpiMutation.variables === kpi.id,
                            onSelect: () => handleDeleteKpi(kpi),
                          },
                        ]}
                      />
                    }
                  />
                ))}
              </StaggerGrid>
            ) : (
              <KpiEmptyState onAdd={handleCreateKpi} />
            )}
          </AnimatedPanel>
        </div>
      )}

      {activeTab === 'analytics' && (
        <div className="dashboard-section">
          <DashboardCharts charts={analytics?.charts} isLoading={analyticsLoading} />
        </div>
      )}
      </TabFade>
      </div>
      </div>

      <Modal
        open={showKpiModal}
        onClose={() => setShowKpiModal(false)}
        ariaLabel={editingKpi ? 'Edit KPI' : 'Create KPI'}
      >
        <h2>{editingKpi ? 'Edit KPI' : 'Create KPI'}</h2>
        <form onSubmit={handleSubmitKpi}>
              {kpiError && <div className="error">{kpiError}</div>}
              <div className="form-group">
                <label htmlFor="kpi-name">Name</label>
                <input
                  id="kpi-name"
                  type="text"
                  value={kpiForm.name}
                  onChange={(e) => setKpiForm({ ...kpiForm, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="kpi-description">Description</label>
                <textarea
                  id="kpi-description"
                  value={kpiForm.description}
                  onChange={(e) => setKpiForm({ ...kpiForm, description: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label htmlFor="kpi-metric-type">Metric Type</label>
                {isLoadingMetricTypes ? (
                  <select id="kpi-metric-type" disabled>
                    <option>Loading metric types...</option>
                  </select>
                ) : metricTypesError ? (
                  <div className="error">Error loading metric types. Please refresh.</div>
                ) : !metricTypes?.length ? (
                  <div className="error">No metric types available for your role.</div>
                ) : (
                  <select
                    id="kpi-metric-type"
                    value={kpiForm.metric_type}
                    onChange={(e) => setKpiForm({ ...kpiForm, metric_type: e.target.value })}
                    required
                    aria-describedby={
                      kpiForm.metric_type === 'custom_filter' ? 'kpi-metric-type-hint' : undefined
                    }
                    disabled={!!editingKpi && editingKpi.metric_type === 'custom_filter'}
                  >
                    <option value="">Select metric type</option>
                    {metricTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                )}
                {kpiForm.metric_type === 'custom_filter' && (
                  <p className="form-hint" id="kpi-metric-type-hint">
                    Save custom filters from the Candidates page.
                  </p>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="kpi-display-order">Display Order</label>
                <input
                  id="kpi-display-order"
                  type="number"
                  min="0"
                  value={kpiForm.display_order}
                  onChange={(e) => setKpiForm({ ...kpiForm, display_order: e.target.value })}
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowKpiModal(false)}>
                  Cancel
                </button>
                <LoadingButton
                  type="submit"
                  className="btn btn-primary"
                  icon={editingKpi ? FiEdit : FiPlus}
                  loading={createKpiMutation.isLoading || updateKpiMutation.isLoading}
                  loadingLabel={editingKpi ? 'Updating…' : 'Creating…'}
                >
                  {editingKpi ? 'Update' : 'Create'}
                </LoadingButton>
              </div>
            </form>
      </Modal>
    </div>
  );
};

export default Dashboard;
